import { Plus } from 'lucide-react';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { ASSISTANCE_LABELS } from '@/features/patients/types';
import { useAppointments } from '@/features/queue/api';
import type { Appointment } from '@/features/queue/types';
import { AppArrivalBadges } from '@/pages/queue/AppCheckInSummary';
import { BookingDrawer } from './BookingDrawer';
import { STATUS_LABEL, depositLabel } from './booking-format';
import { NewBookingDrawer } from './NewBookingDrawer';
import { dayLabel, whenLabel } from './time';
import styles from './calendar.module.css';

const REPEAT_LABEL = {
  once: null,
  every_3_months: 'Every 3 months',
  every_6_months: 'Every 6 months',
} as const;

/** "Calendar · bookings" (#16): upcoming bookings with service, doctor, deposit and status. */
export function BookingsTab() {
  const { hasPermission } = useSession();
  const canManage = hasPermission('appointments:manage');
  const { data, isLoading, error, refetch } = useAppointments(undefined, 62);
  const [booking, setBooking] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const rows = (data?.appointments ?? []).filter(
    (a) => a.status === 'booked' || a.status === 'checked_in',
  );

  const columns: Column<Appointment>[] = [
    {
      key: 'when',
      header: 'When',
      render: (a) => (
        <>
          <p className={styles.name}>{whenLabel(a.startsAt)}</p>
          <p className={styles.sub}>{a.branch.name}</p>
        </>
      ),
    },
    {
      key: 'patient',
      header: 'Patient',
      render: (a) => (
        <>
          <p className={styles.name}>{a.patient.fullName}</p>
          {a.patient.assistanceNeeds.map((n) => (
            <Badge key={n} tone="info">
              {ASSISTANCE_LABELS[n].split(':')[0]}
            </Badge>
          ))}
        </>
      ),
    },
    {
      key: 'service',
      header: 'Service',
      render: (a) => (
        <>
          <p>{a.service.name}</p>
          {a.travelDate && (
            <p className={styles.sub}>Travel {dayLabel(a.travelDate)}</p>
          )}
          {REPEAT_LABEL[a.repeat] && (
            <Badge tone="info">{REPEAT_LABEL[a.repeat]}</Badge>
          )}
        </>
      ),
    },
    {
      key: 'doctor',
      header: 'Doctor',
      render: (a) => a.doctor?.fullName ?? 'Any',
    },
    { key: 'deposit', header: 'Deposit', render: depositLabel },
    {
      key: 'status',
      header: 'Status',
      render: (a) => (
        <>
          <Badge
            tone={
              a.status === 'checked_in'
                ? 'success'
                : a.rebook
                  ? 'danger'
                  : 'neutral'
            }
          >
            {a.rebook && a.status === 'booked'
              ? 'Asked to rebook'
              : a.status === 'checked_in' && a.arrivedLate
                ? 'Arrived late'
                : STATUS_LABEL[a.status]}
          </Badge>
          <AppArrivalBadges
            lateNotice={a.lateNotice}
            appCheckIn={a.appCheckIn}
          />
        </>
      ),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (a) => (
        <Button size="sm" variant="ghost" onClick={() => setOpen(a.id)}>
          Details
        </Button>
      ),
    },
  ];

  return (
    <Card
      title="Upcoming bookings"
      flush
      actions={
        <>
          <span className={styles.sub}>
            Recurring follow-ups book the next one when a visit is seen
          </span>
          {canManage && (
            <Button
              size="sm"
              variant="primary"
              icon={<Plus size={14} />}
              onClick={() => setBooking(true)}
            >
              Book
            </Button>
          )}
        </>
      }
    >
      <DataTable
        caption="Upcoming bookings"
        columns={columns}
        rows={data ? rows : undefined}
        rowKey={(a) => a.id}
        loading={isLoading}
        error={error}
        onRetry={() => void refetch()}
        empty={
          <EmptyState
            title="No upcoming bookings"
            body="New bookings appear here."
          />
        }
      />
      <NewBookingDrawer open={booking} onClose={() => setBooking(false)} />
      <BookingDrawer id={open} onClose={() => setOpen(null)} />
    </Card>
  );
}
